import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Store, Wrench, ExternalLink } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, SectionHeading } from "@/components/motion";

export default function TPostGuidePage() {
  return (
    <div data-testid="tpost-guide-page">
      <PageHeader
        kicker="Where to Buy T-Posts"
        title="The right post, under ten bucks, everywhere"
        sub="T-posts aren't included in the kits because they're heavy, cheap and sold in nearly every town in America. Here's exactly what to buy and where."
        image="/assets/img/w-t-post-img-1779psde.jpg"
      />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="clip-corner h-full border border-girder bg-steel/50 p-8" data-testid="tpost-buy">
              <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-hivis"><CheckCircle2 className="h-4 w-4" /> Buy This</p>
              <h3 className="mt-4 font-display text-3xl font-black uppercase text-slate-100">6'-6" studded T-post</h3>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                <li className="flex gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-hivis" /> Minimum 1.25 lbs per foot (TP125) or 1.33 lbs per foot (TP133)</li>
                <li className="flex gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-hivis" /> 6'-6" length leaves proper depth below grade for wind loading</li>
                <li className="flex gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-hivis" /> Usually under $10 each</li>
                <li className="flex gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-hivis" /> Standard fence post — no special order</li>
              </ul>
            </div>
          </Reveal>
          <Reveal i={1}>
            <div className="clip-corner h-full border border-girder bg-steel/50 p-8" data-testid="tpost-avoid">
              <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-red-400"><XCircle className="h-4 w-4" /> Avoid This</p>
              <h3 className="mt-4 font-display text-3xl font-black uppercase text-slate-100">"Garden variety" posts</h3>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                <li className="flex gap-3"><XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" /> Light-duty posts from home improvement garden sections</li>
                <li className="flex gap-3"><XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" /> Anything under 1.25 lbs per foot — light duty does not fit Banner Bar T-clips</li>
                <li className="flex gap-3"><XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" /> Shorter than 6'-6" — not enough post below grade</li>
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="mt-16">
          <SectionHeading kicker="Retailers" title="Where to pick them up" />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="tpost-retailers">
            {[
              { name: "Tractor Supply", note: "Nationwide, TP125/TP133 in stock, delivery to your shop available", url: "https://www.tractorsupply.com/tsc/product/studded-t-post-6-ft-125-lb-per-foot-3609112" },
              { name: "Big R Stores", note: "Farm & ranch stores across the West and Midwest", url: "https://bigronline.com/hutchison-western-green-tee-post-133/" },
              { name: "Murdoch's", note: "Rocky Mountain region farm supply", url: "https://www.murdochs.com/products/farm-ranch/fencing-gates/fence-posts/cmc-southern-post-133-lbs-t-post/" },
              { name: "Home Depot", note: "Everbilt 6'-6\" green steel T-post with anchor plate", url: "https://www.homedepot.com/p/Everbilt-1-3-4-in-x-3-1-2-in-x-6-1-2-ft-Green-Steel-Fence-T-Post-with-Anchor-Plate-901178EB/205960895" },
              { name: "McCoy's Building Supply", note: "6'-6\" studded T-posts across Texas and the South", url: "https://www.mccoys.com/shop/p/06060603/w-silver-tr133060601uw-t-style-fence-post-6-12-ft-h-2-in-w-2-in-l-rail-steel-green-acrylic-coated" },
              { name: "Local farm & ranch", note: "Nearly every town has one — ask for 1.25 lb/ft studded posts" },
            ].map((r, i) => {
              const card = (
                <>
                  <div className="flex items-start justify-between">
                    <Store className="h-6 w-6 text-hivis" />
                    {r.url && <ExternalLink className="h-4 w-4 text-slate-500 transition-colors group-hover:text-hivis" />}
                  </div>
                  <h4 className="mt-3 font-display text-xl font-bold uppercase text-slate-100">{r.name}</h4>
                  <p className="mt-2 text-sm text-slate-400">{r.note}</p>
                  {r.url && <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-hivis">View the exact post</p>}
                </>
              );
              const cls = "group block h-full clip-corner border border-girder bg-obsidian p-6 transition-colors hover:border-hivis/60";
              return (
                <Reveal key={r.name} i={i % 3}>
                  {r.url
                    ? <a href={r.url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`retailer-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{card}</a>
                    : <div className={cls} data-testid="retailer-local-farm-ranch">{card}</div>}
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal className="mt-16">
          <div className="clip-corner grid overflow-hidden border border-girder bg-steel/50 sm:grid-cols-2" data-testid="tpost-tools">
            <img src="/assets/img/w_install_tpost.jpg" alt="Driving a T-post with a T-post driver" loading="lazy" className="h-full w-full object-cover" />
            <div className="flex flex-col items-start justify-center gap-4 p-8">
              <Wrench className="h-8 w-8 shrink-0 text-hivis" />
              <h4 className="font-display text-2xl font-bold uppercase text-slate-100">Don't forget the driver</h4>
              <p className="text-sm text-slate-400">A T-post driver is required for proper installs — and a T-post puller makes removal fast without bending posts. Both are available at the same retailers. Gloves and hearing protection strongly recommended.</p>
              <Link to="/installation" data-testid="tpost-install-link" className="clip-corner-sm bg-hivis px-6 py-3 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">Watch the install</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
